import React, { useState } from 'react';
import { 
  HelpCircle, Book, ChevronRight, ChevronDown, Shield, 
  Cloud, Search, Globe, X, ExternalLink, MessageCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';

// FAQ and Guide data for each module
const HELP_DATA = {
  grc: {
    name: 'GRC Module',
    icon: Shield,
    color: 'blue',
    description: 'Governance, Risk & Compliance management',
    quickStart: [
      'Enable compliance frameworks from the Frameworks page',
      'Review and customize controls for your organization',
      'Upload evidence and documents for audit readiness',
      'Track risks in the Risk Register',
      'Generate compliance reports'
    ],
    faq: [
      {
        q: 'How do I enable a new compliance framework?',
        a: 'Go to GRC > Frameworks, find the framework you want (e.g., ISO 27001, SOC 2), and click "Enable". Controls will be auto-populated.'
      },
      {
        q: 'How do I track evidence for controls?',
        a: 'Navigate to GRC > Evidence, click "Upload Evidence", select the relevant control, and attach your documentation. Set review frequency for recurring audits.'
      },
      {
        q: 'Can I create custom policies?',
        a: 'Yes! Go to GRC > Policies > Create Policy. You can start from scratch or use our pre-built templates for common policies like Information Security, Access Control, etc.'
      },
      {
        q: 'How is the compliance score calculated?',
        a: 'The score is based on: Control implementation (40%), Policy coverage (20%), Risk posture (20%), Vendor assessments (10%), and Document coverage (10%).'
      },
      {
        q: 'How do I request a framework not in my list?',
        a: 'Click "Request Framework" on any unavailable framework. Your admin will review and approve the request.'
      }
    ]
  },
  amidala: {
    name: 'Amidala CSPM',
    icon: Cloud,
    color: 'purple',
    description: 'Cloud Security Posture Management',
    quickStart: [
      'Connect your cloud accounts (AWS, Azure, GCP)',
      'Run your first cloud security scan',
      'Review misconfigurations and findings',
      'Set up automated remediation workflows',
      'Configure real-time alerts'
    ],
    faq: [
      {
        q: 'How do I connect my AWS account?',
        a: 'Go to Amidala > Cloud Accounts > Add Account. Select AWS, provide your AWS Access Key ID and Secret, or use IAM role-based access for better security.'
      },
      {
        q: 'What does the security score represent?',
        a: 'The security score (0-100) reflects your cloud security posture based on misconfigurations found, compliance with best practices, and risk severity.'
      },
      {
        q: 'How often should I run scans?',
        a: 'We recommend daily scans for production environments. You can set up scheduled scans in the Cloud Accounts settings.'
      },
      {
        q: 'Can I ignore certain findings?',
        a: 'Yes, you can suppress or acknowledge findings with a reason. Go to the finding details and click "Suppress" with a justification.'
      },
      {
        q: 'Does Amidala support multi-cloud?',
        a: 'Yes! Amidala supports AWS, Azure, and GCP. You can connect multiple accounts from different providers and get a unified security view.'
      }
    ]
  },
  litesabre: {
    name: 'LiteSabre Scanner',
    icon: Search,
    color: 'emerald',
    description: 'SAST, SCA, and DAST security scanning',
    quickStart: [
      'Start a new scan from the Scanner page',
      'Choose scan type: SAST (code), SCA (dependencies), or DAST (web app)',
      'Review vulnerabilities and findings',
      'Export reports in PDF or JSON format',
      'Create custom scanning rules'
    ],
    faq: [
      {
        q: 'What is the difference between SAST, SCA, and DAST?',
        a: 'SAST analyzes source code for vulnerabilities. SCA checks dependencies for known CVEs. DAST tests running web applications for security flaws.'
      },
      {
        q: 'How do I scan a web application?',
        a: 'Go to LiteSabre Pro > New Scan, enter the target URL, configure scan depth and options, then click Start Scan. Enable "Include Subdomains" for comprehensive coverage.'
      },
      {
        q: 'Can I create custom scanning rules?',
        a: 'Yes! Go to LiteSabre Pro > Custom Rules tab. You can create rules with custom payloads, detection patterns, and use our Regex Builder for pattern creation.'
      },
      {
        q: 'What is an SBOM report?',
        a: 'SBOM (Software Bill of Materials) lists all components and dependencies in your software, helping track vulnerabilities and license compliance.'
      },
      {
        q: 'How do I reduce false positives?',
        a: 'Review each finding, mark false positives with a reason, and fine-tune your custom rules. The scanner learns from your feedback over time.'
      }
    ]
  },
  osint: {
    name: 'OSINT Module',
    icon: Globe,
    color: 'amber',
    description: 'Open Source Intelligence gathering',
    quickStart: [
      'Configure your API keys for external services',
      'Start a new intelligence gathering session',
      'Search across multiple data sources',
      'Export findings and reports',
      'Set up monitoring for continuous intelligence'
    ],
    faq: [
      {
        q: 'What data sources does OSINT search?',
        a: 'OSINT searches public records, social media, domain/IP data, breach databases, dark web mentions, and other open sources based on your configured API keys.'
      },
      {
        q: 'How do I configure API keys?',
        a: 'Go to OSINT > Settings > API Keys. Add keys for services like Shodan, VirusTotal, Have I Been Pwned, etc. Each service requires its own API key.'
      },
      {
        q: 'Is the data collection legal?',
        a: 'OSINT only collects publicly available information. Always ensure you have proper authorization and comply with local laws when investigating targets.'
      },
      {
        q: 'Can I monitor a domain continuously?',
        a: 'Yes! Set up monitoring alerts to be notified when new information is discovered about your monitored domains, IPs, or entities.'
      },
      {
        q: 'How do I export OSINT findings?',
        a: 'From any investigation, click Export to download findings in PDF, JSON, or CSV format for further analysis or reporting.'
      }
    ]
  }
};

// Floating Help Button Component
export function HelpButton({ currentModule = 'grc' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState(currentModule);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  const moduleData = HELP_DATA[selectedModule] || HELP_DATA.grc;
  const ModuleIcon = moduleData.icon;

  return (
    <>
      {/* Floating Help Button */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="fixed bottom-6 right-20 h-12 w-12 rounded-full shadow-lg bg-white hover:bg-gray-50 border-gray-200 z-50"
            data-testid="help-button"
          >
            <HelpCircle className="h-6 w-6 text-gray-600" />
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-80 p-0" 
          align="end" 
          side="top"
          sideOffset={16}
        >
          <div className="p-4 border-b bg-gradient-to-r from-blue-50 to-purple-50">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Book className="w-5 h-5 text-blue-600" />
              Help & Support
            </h3>
            <p className="text-xs text-gray-500 mt-1">Quick access to guides and FAQs</p>
          </div>
          
          {/* Module Quick Links */}
          <div className="p-3 space-y-1">
            {Object.entries(HELP_DATA).map(([key, data]) => {
              const Icon = data.icon;
              return (
                <button
                  key={key}
                  onClick={() => { setSelectedModule(key); setIsOpen(true); }}
                  className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors text-left"
                  data-testid={`help-${key}`}
                >
                  <div className={`p-2 rounded-lg bg-${data.color}-100`}>
                    <Icon className={`w-4 h-4 text-${data.color}-600`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{data.name}</p>
                    <p className="text-xs text-gray-500 truncate">{data.description}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </button>
              );
            })}
          </div>
          
          {/* Contact Support Link */}
          <div className="p-3 border-t bg-gray-50">
            <a 
              href="mailto:support@beskarit.com" 
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
            >
              <MessageCircle className="w-4 h-4" />
              Contact Support
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </PopoverContent>
      </Popover>

      {/* Full Guide Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] p-0">
          <DialogHeader className="p-6 pb-4 border-b bg-gradient-to-r from-blue-50 to-purple-50">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-xl bg-${moduleData.color}-100`}>
                <ModuleIcon className={`w-6 h-6 text-${moduleData.color}-600`} />
              </div>
              <div>
                <DialogTitle className="text-xl">{moduleData.name} Guide</DialogTitle>
                <p className="text-sm text-gray-500 mt-1">{moduleData.description}</p>
              </div>
            </div>
            
            {/* Module Tabs */}
            <div className="flex gap-2 mt-4">
              {Object.entries(HELP_DATA).map(([key, data]) => (
                <button
                  key={key}
                  onClick={() => { setSelectedModule(key); setExpandedFaq(null); }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    selectedModule === key 
                      ? `bg-${data.color}-100 text-${data.color}-700` 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {data.name}
                </button>
              ))}
            </div>
          </DialogHeader>
          
          <ScrollArea className="max-h-[60vh]">
            <div className="p-6 space-y-6">
              {/* Quick Start Guide */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    Quick Start
                  </Badge>
                </h4>
                <ol className="space-y-2">
                  {moduleData.quickStart.map((step, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm">
                      <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold shrink-0">
                        {i + 1}
                      </span>
                      <span className="text-gray-700 pt-0.5">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
              
              {/* FAQ Section */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                    Frequently Asked Questions
                  </Badge>
                </h4>
                <div className="space-y-2">
                  {moduleData.faq.map((item, i) => (
                    <div 
                      key={i} 
                      className="border rounded-lg overflow-hidden"
                    >
                      <button
                        onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                        className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors"
                      >
                        <span className="text-sm font-medium text-gray-900 pr-4">{item.q}</span>
                        {expandedFaq === i ? (
                          <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                        )}
                      </button>
                      {expandedFaq === i && (
                        <div className="px-3 pb-3 pt-0">
                          <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                            {item.a}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default HelpButton;
